import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, MessageCircle, CalendarPlus, Play } from "lucide-react";
import { VideoModal, hasVideo } from "@/components/VideoModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export const MentorCard = ({ mentor, index, onBook }) => {
    const [videoOpen, setVideoOpen] = useState(false);
    return (
        <motion.div
            data-testid={`mentor-card-${mentor.username}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="group relative overflow-hidden rounded-2xl border border-border bg-card transition-all duration-500 hover:border-primary/50 hover:shadow-[0_0_30px_hsl(119_99%_46%/0.12)]"
        >
            <div className="relative aspect-[4/5] overflow-hidden">
                <img
                    src={resolveUrl(mentor.photo_url) || "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=940"}
                    alt={mentor.display_name}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                /> 
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
                {mentor.is_director && (
                    <span className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
                        Director
                    </span>
                )}
                {mentor.best_month && !mentor.is_director && (
                    <span className="absolute right-4 top-4 rounded-full bg-black/60 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary backdrop-blur-sm">
                        {mentor.best_month}
                    </span>
                )}
            </div>

            <div className="relative -mt-14 p-5">
                <h3 className="text-xl font-bold tracking-tight text-foreground">{mentor.display_name}</h3>
                <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                    {mentor.specialty}
                </p>
                {mentor.country && (
                    <p className="mt-1 text-xs text-muted-foreground">{mentor.country}</p>
                )}
                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                    {mentor.bio}
                </p>

                <div className="mt-4 flex items-center gap-2">
                    <button
                        data-testid={`book-mentor-${mentor.username}`}
                        onClick={() => onBook(mentor)}
                        className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-primary-foreground transition-all hover:brightness-110 active:scale-95"
                    >
                        <CalendarPlus size={14} /> Agendar
                    </button>
                    {mentor.instagram && (
                        <a
                            href={`https://instagram.com/${mentor.instagram}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`ig-${mentor.username}`}
                            className="rounded-full border border-border p-2.5 text-muted-foreground transition-all hover:border-primary hover:text-primary"
                        >
                            <Instagram size={16} />
                        </a>
                    )}
                    {mentor.whatsapp && (
                        <a
                            href={`https://wa.me/${mentor.whatsapp}?text=${encodeURIComponent(
                                `Hola ${mentor.display_name}, quiero agendar una llamada.`
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`wa-${mentor.username}`}
                            className="rounded-full border border-border p-2.5 text-muted-foreground transition-all hover:border-primary hover:text-primary"
                        >
                            <MessageCircle size={16} />
                        </a>
                    )}
                </div>
                {hasVideo(mentor) && (
                    <button onClick={() => setVideoOpen(true)} data-testid={`play-video-${mentor.username}`} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full border border-border py-2 text-xs font-semibold text-muted-foreground transition-all hover:border-primary hover:text-primary">
                        <Play size={13} fill="currentColor" /> Ver testimonio
                    </button>
                )}
            </div>
            <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} mentor={mentor} />
        </motion.div>
    );
};
